import * as classnames from "classnames";
import { isEqual } from "lodash";
import * as React from "react";
import { Switch, Route, Redirect } from "react-router-dom";

import {
  Loading,
  Modal,
  PoorHealth,
  Msg,
  PageVisibility,
} from "@shared/components";
import { VisibilityEvent } from "@shared/hooks/usePageVisible";
import {
  ApplicantExamModel,
  ApplicantChallengeContextModel,
  ApplicantEditorContextModel,
  HealthCheckModel,
} from "@shared/models";
import { ApplicantExamStatus } from "@shared/services/enums";
import Message from "@shared/services/message";
import { parseUrl } from "@shared/services/queryString";
import * as Survey from "@shared/services/survey";

import Error from "../../common/error/error/Error";
import SystemHealth from "../../common/systemHealth/SystemHealth.connect";
import ExamFinished from "../examFinished/ExamFinished";
import Signin from "../signin/Signin.connect";
import {
  EditorChallengeList,
  EditorExamDetail,
  EditorMain,
  EditorSideMenu,
  EditorMessagebar,
  EditorAppealComment,
  TimerHeadquarter,
  CodeFile,
  WebcamScreenshotUploader,
} from "./partials";

interface TabTracking {
  isTabFocused: boolean;
  isEditorFocused: boolean;
  isAppFocused: boolean;
}

/**
 * Prop interface
 */
export interface EditorProps {
  error: boolean;
  authenticated: boolean;
  loading: boolean;
  sessionDuplicated: boolean;
  keyEventSessionDuplicated: boolean;
  tutorial: boolean;
  exam: ApplicantExamModel;
  challengeContext: ApplicantChallengeContextModel;
  challengeEditorContext: ApplicantEditorContextModel;
  match: {
    params: {
      token: string;
    };
  };
  challengeSourceFiles: Array<CodeFile>;
  health: HealthCheckModel;
  isUpdatingAppealComment: boolean;
  isUpdateAppealCommentSuccess: boolean;
  isUpdateAppealCommentError: boolean;
  isWebcamEnabled: boolean;

  getExam: () => void;
  submitExam: () => void;
  updateForm: (payload: {}) => void;
  getChallengeContext: (params: {}) => void;
  removeChallengeContext: () => void;
  updateFormAndOpenExam: (payload: {}) => void;
  startChallenge: (payload: {}) => void;
  resetChallenge: (params: {}) => void;
  saveChallenge: (payload: {}, params?: {}) => void;
  switchLanguage: (payload: {}, params: {}) => void;
  switchFrontendTemplate: (payload: {}, params: {}) => void;
  updateEnvVars: (payload: {}, params: {}) => void;
  removeNotification: (payload: {}) => void;
  setEditorHasUnsaved: (payload: {}) => void;
  setEditorReady: () => void;
  setEditorReload: () => void;
  getTutorialExam: (payload: {}) => void;
  getPresigned: (payload: {}, params: {}) => void;
  updateAppealComment: (payload: {}, params: {}) => void;
  healthCheck: () => void;
  switchTakenBy: (payload: {}, params: {}) => void;
  extendSession: () => void;
  updateFaceLivenessSuccess: () => void;
  createSwitchTabEvent: (payload: boolean) => void;
}

/**
 * State interface
 */
export interface EditorState {
  tutorialFinished: boolean;
  isOpenHealth: boolean;
  isLoadSurvey: boolean;
  tabTracking: TabTracking;
  windowEvents: VisibilityEvent[];
}

/**
 * Page component
 */
class Editor extends React.Component<EditorProps, EditorState> {
  extendSessionId: number | null = null;

  constructor(props: EditorProps) {
    super(props);

    this.state = {
      tutorialFinished: false,
      isOpenHealth: false,
      isLoadSurvey: false,
      tabTracking: {
        isTabFocused: true,
        isEditorFocused: true,
        isAppFocused: false,
      },
      windowEvents: ["visibilitychange"],
    };
  }

  public UNSAFE_componentWillMount() {
    if (this.props.tutorial && !this.props.match.params.token) {
      const { lang: language = "ja" } = parseUrl(window.location.search);
      this.props.getTutorialExam({ language });
    }

    this.props.healthCheck();
  }

  public UNSAFE_componentWillReceiveProps(nextProps: EditorProps) {
    if (
      nextProps.tutorial &&
      nextProps.exam.status === ApplicantExamStatus.Submitted
    ) {
      this.setState({
        tutorialFinished: true,
      });
    }

    if (
      !(nextProps.tutorial || nextProps.exam.isTest) &&
      nextProps.exam &&
      nextProps.exam.isFinished()
    ) {
      if (!this.state.isLoadSurvey) {
        Survey.showSurvey({
          uniqueId: nextProps.exam.id.toString(),
          data: nextProps.exam,
          surveyType: "applicant",
        });
        this.setState({ isLoadSurvey: true });
      }
    }
  }

  public componentDidUpdate(
    prevProps: Readonly<EditorProps>,
    prevState: Readonly<EditorState>,
  ) {
    if (
      this.extendSessionId === null &&
      !prevProps.exam.isStarted() &&
      this.props.exam.isStarted()
    ) {
      this.extendSessionId = window.setInterval(
        this.props.extendSession,
        Number(process.env.SESSION_EXTEND_INTERVAL_MS),
      );
    }

    if (
      this.extendSessionId !== null &&
      !prevProps.exam.isFinished() &&
      this.props.exam.isFinished()
    ) {
      clearInterval(this.extendSessionId);
    }

    if (
      prevState.tabTracking.isTabFocused !== this.state.tabTracking.isTabFocused
    ) {
      this.props.createSwitchTabEvent(this.state.tabTracking.isTabFocused);
    }
  }

  public componentWillUnmount() {
    if (this.extendSessionId !== null) {
      clearInterval(this.extendSessionId);
    }
  }

  public shouldComponentUpdate(nextProps: EditorProps, nextState: EditorState) {
    return !isEqual(nextProps, this.props) || !isEqual(nextState, this.state);
  }

  public render() {
    if (this.props.error) {
      return (
        <Error
          hasBackButton={false}
          location={{
            state: {
              error: Message.getMessageByKey("message.applicant.examNotFound"),
            },
          }}
        />
      );
    }

    const rootStyle = classnames("code-editor");

    const {
      authenticated,
      loading,
      exam,
      tutorial,
      health,
      challengeContext,
      challengeEditorContext,
      isUpdatingAppealComment,
      isUpdateAppealCommentSuccess,
      isUpdateAppealCommentError,
      sessionDuplicated,
      keyEventSessionDuplicated,

      getExam,
      submitExam,
      updateForm,
      updateFormAndOpenExam,
      startChallenge,
      resetChallenge,
      saveChallenge,
      switchLanguage,
      switchFrontendTemplate,
      updateEnvVars,
      getChallengeContext,
      removeChallengeContext,
      removeNotification,
      setEditorHasUnsaved,
      setEditorReady,
      setEditorReload,
      getPresigned,
      challengeSourceFiles,
      updateAppealComment,
      switchTakenBy,
    } = this.props;

    const { windowEvents } = this.state;

    if (health.checked() && !health.passed()) {
      return (
        <div className={rootStyle}>
          <SystemHealth
            isOpen={true}
            onClose={() => undefined}
            canCancel={false}
          />
        </div>
      );
    }

    if (exam.exam.isEmpty()) {
      return (
        <div className={rootStyle}>
          <Loading isOpen={loading || authenticated === undefined} />
          <Signin isOpen={authenticated === false} />
        </div>
      );
    }

    // Show the exam finished page instead.
    if (
      [
        ApplicantExamStatus.Canceled,
        ApplicantExamStatus.Submitted,
        ApplicantExamStatus.InReview,
        ApplicantExamStatus.Passed,
        ApplicantExamStatus.Failed,
      ].includes(exam.status)
    ) {
      return <ExamFinished exam={exam} />;
    }

    return (
      <div className={rootStyle}>
        <Loading isOpen={loading} />
        <TimerHeadquarter />
        <WebcamScreenshotUploader />
        {health.checked() && !health.passed() && (
          <div className="code-editor__bad-health">
            <PoorHealth
              health={this.props.health}
              showJumpButton={true}
              onOpenHealth={this.onOpenHealth}
            />
          </div>
        )}
        <div className="code-editor__menu">
          <EditorSideMenu exam={exam} tutorial={tutorial} />
        </div>
        <div className="code-editor__main">
          <EditorMessagebar />
          {exam.isStarted() ? (
            <Switch>
              <Route
                path="/:orgname/exams/:token"
                exact={true}
                render={() => (
                  <EditorExamDetail
                    exam={exam}
                    tutorial={tutorial}
                    updateFormAndOpenExam={updateFormAndOpenExam}
                    updateForm={updateForm}
                    submitExam={submitExam}
                    health={health}
                  />
                )}
              />
              <Route
                path="/:orgname/exams/:token/(challenges|submit)"
                exact={true}
                render={() => (
                  <EditorChallengeList
                    exam={exam}
                    tutorial={tutorial}
                    submitExam={submitExam}
                  />
                )}
              />
              <Route
                path="/:orgname/exams/:token/appeal/:challengeId"
                render={({ match }) => (
                  <EditorAppealComment
                    exam={exam}
                    challengeId={parseInt(match.params.challengeId, 10)}
                    getChallengeContext={getChallengeContext}
                    removeChallengeContext={removeChallengeContext}
                    getPresigned={getPresigned}
                    challengeContext={challengeContext}
                    files={challengeSourceFiles}
                    submitExam={submitExam}
                  />
                )}
              />
              <Route
                path="/:orgname/exams/:token/challenges/:challengeId/:currentPage?"
                exact={true}
                render={({ match }) => (
                  <EditorMain
                    exam={exam}
                    tutorial={tutorial}
                    getExam={getExam}
                    challengeContext={challengeContext}
                    challengeEditorContext={challengeEditorContext}
                    isUpdatingAppealComment={isUpdatingAppealComment}
                    isUpdateAppealCommentError={isUpdateAppealCommentError}
                    isUpdateAppealCommentSuccess={isUpdateAppealCommentSuccess}
                    startChallenge={startChallenge}
                    resetChallenge={resetChallenge}
                    saveChallenge={saveChallenge}
                    switchLanguage={switchLanguage}
                    switchFrontendTemplate={switchFrontendTemplate}
                    submitExam={submitExam}
                    updateEnvVars={updateEnvVars}
                    getChallengeContext={getChallengeContext}
                    removeChallengeContext={removeChallengeContext}
                    removeNotification={removeNotification}
                    setEditorHasUnsaved={setEditorHasUnsaved}
                    setEditorReady={setEditorReady}
                    setEditorReload={setEditorReload}
                    challengeId={parseInt(match.params.challengeId, 10)}
                    currentPage={
                      match.params.currentPage
                        ? parseInt(match.params.currentPage, 10)
                        : undefined
                    }
                    updateAppealComment={updateAppealComment}
                    switchTakenBy={switchTakenBy}
                  />
                )}
              />
              <Route
                path="/tutorial"
                exact={true}
                render={() => (
                  <Redirect
                    push={false}
                    to={`/tutorial/exams/${exam.urlToken}`}
                  />
                )}
              />
            </Switch>
          ) : (
            <Switch>
              <Route
                path="/:orgname/exams/:token"
                exact={true}
                render={({}) => (
                  <EditorExamDetail
                    exam={exam}
                    submitExam={submitExam}
                    tutorial={tutorial}
                    updateFormAndOpenExam={updateFormAndOpenExam}
                    updateForm={updateForm}
                    health={health}
                  />
                )}
              />
              <Route
                path="/:orgname/exams/:token/"
                strict={true}
                render={({ match }) => (
                  <Redirect
                    push={false}
                    to={`/${match.params.orgname}/exams/${match.params.token}`}
                  />
                )}
              />
              <Route
                path="/tutorial"
                exact={true}
                render={() => (
                  <Redirect
                    push={false}
                    to={`/tutorial/exams/${exam.urlToken}`}
                  />
                )}
              />
            </Switch>
          )}
        </div>
        <Modal
          // TODO dead code
          className="code-editor__tutorial-finished"
          title={<Msg id={"final-exam-submit.title"} />}
          isOpen={this.state.tutorialFinished}
          hasOkButton={false}
          cancelButtonLabel={<Msg id={"close"} />}
          additionalFooterContent={<Msg id={"editor.tutorialFinished.body"} />}
          onClose={() => this.setState({ tutorialFinished: false })}
          onClickCancel={() => this.setState({ tutorialFinished: false })}
        />
        {(sessionDuplicated || keyEventSessionDuplicated) && (
          <Modal
            isOpen={true}
            className="code-editor__duplicated"
            title={<Msg id={"editor.duplicated.title"} />}
            okButtonLabel={<Msg id={"action.reload"} />}
            onClickOk={() => window.location.reload()}
            hasCancelButton={false}
            hasCloseButton={false}
          >
            <Msg
              id={
                sessionDuplicated
                  ? "editor.duplicated.body"
                  : "editor.keyEvent.duplicated.body"
              }
            />
          </Modal>
        )}
        <SystemHealth
          isOpen={this.state.isOpenHealth}
          onClose={this.onCloseHealth}
        />
        <PageVisibility
          events={windowEvents}
          visible={Boolean(windowEvents)}
          onVisibilityChange={this.onAppVisibilityChange}
        />
      </div>
    );
  }

  private onOpenHealth = () => {
    this.setState({ isOpenHealth: true });
  };

  private onCloseHealth = () => {
    this.setState({ isOpenHealth: false });
  };

  private onAppVisibilityChange = (value: boolean) => {
    const newState = {
      ...this.state.tabTracking,
      isTabFocused: value,
    } as TabTracking;

    this.setState({ tabTracking: newState });
  };
}

// For testing
export { Editor };

export default Editor;
